import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { generate90DayPlan, mockPlanInput } from '../data/improvementPlanLogic';

export const ImprovementPlanEngine = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [activePhase, setActivePhase] = useState(1);

  const startGeneration = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setPlan(generate90DayPlan(mockPlanInput, "TechCorp AS"));
      setIsGenerating(false);
    }, 2000);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI 90 Day Improvement Plan</h1>
          <p className="text-slate-500 mt-1">Automatisk generering av strategisk veikart for organisasjonsutvikling.</p>
        </div>
        {plan && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={<SafeIcon name="Download" />}>PDF Plan</Button>
            <Button variant="primary" icon={<SafeIcon name="Share2" />}>Del med Ledelsen</Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!plan ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[60vh] flex items-center justify-center">
            <Card className="bg-slate-900 border-none text-white max-w-2xl w-full overflow-hidden relative shadow-3xl">
              <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] -mr-32 -mt-32"></div>
              <CardContent className="p-20 text-center space-y-10 relative z-10">
                <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md">
                  <SafeIcon name="Target" className="text-4xl text-indigo-400" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-4xl font-black tracking-tighter uppercase">Klar for vekstplan</h2>
                  <p className="text-indigo-100/60 text-lg leading-relaxed">
                    Basert på siste kartleggingsdata vil AI-motoren nå designe et 90-dagers forløp delt inn i tre kritiske faser.
                  </p>
                </div>
                <Button size="lg" onClick={startGeneration} disabled={isGenerating} className="px-16 py-8 text-xl font-black uppercase tracking-widest">
                  {isGenerating ? "Genererer Plan..." : "Start Plan-Engine"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            {/* Executive Summary */}
            <Card className="bg-indigo-50 border-indigo-100 border-l-4 border-l-indigo-600">
              <CardHeader title="Executive Summary" icon={<SafeIcon name="FileText" className="text-indigo-600" />} />
              <CardContent>
                <p className="text-xl text-indigo-900 leading-relaxed font-serif italic">"{plan.executiveSummary}"</p>
              </CardContent>
            </Card>

            {/* Phase Selector Tabs */}
            <div className="flex gap-4 p-1 bg-slate-100 rounded-[24px] w-fit">
              {plan.phases.map((phase) => (
                <button
                  key={phase.id}
                  onClick={() => setActivePhase(phase.id)}
                  className={`px-8 py-4 rounded-[20px] text-xs font-black uppercase tracking-widest transition-all ${activePhase === phase.id ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Fase {phase.id}: {phase.range}
                </button>
              ))}
            </div>

            {/* Active Phase View */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-none shadow-xl overflow-hidden">
                  <div className={`h-2 bg-${plan.phases[activePhase - 1].color}-500`} />
                  <CardHeader 
                    title={plan.phases[activePhase - 1].title} 
                    subtitle={`Hovedfokus for dag ${plan.phases[activePhase - 1].range.split('-')[0].split(' ')[1]} til ${plan.phases[activePhase - 1].range.split('-')[1]}`}
                  />
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                          <tr>
                            <th className="px-6 py-4">Område / Tiltak</th>
                            <th className="px-6 py-4">Forretningsmessig rasjonale</th>
                            <th className="px-6 py-4">Ansvar</th>
                            <th className="px-6 py-4">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {plan.phases[activePhase - 1].actions.map((action, i) => (
                            <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-5">
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 mb-1 block w-fit`}>{action.area}</span>
                                <p className="text-sm font-bold text-slate-900 leading-snug">{action.action}</p>
                              </td>
                              <td className="px-6 py-5">
                                <p className="text-xs text-slate-500 italic leading-relaxed max-w-xs">{action.rationale}</p>
                              </td>
                              <td className="px-6 py-5">
                                <div className="space-y-1">
                                  <p className="text-xs font-bold text-slate-800">{action.responsible}</p>
                                  <p className="text-[10px] text-indigo-600 font-black uppercase">{action.deadline}</p>
                                </div>
                              </td>
                              <td className="px-6 py-5">
                                <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${action.status === 'Kritisk' ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
                                  {action.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar Info */}
              <div className="space-y-6">
                <Card className="bg-slate-900 border-none text-white">
                  <CardHeader title="Leadership Goals" icon={<SafeIcon name="Award" className="text-indigo-400" />} />
                  <CardContent className="space-y-4">
                    {[
                      "Kommunisere med tydelighet",
                      "Modellere sårbarhet og trygghet",
                      "Prioritere operativ skjerming"
                    ].map((goal, i) => (
                      <div key={i} className="flex gap-3 text-sm text-slate-300">
                        <SafeIcon name="Check" className="text-indigo-500 shrink-0 mt-1" />
                        <span>{goal}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <div className="bg-emerald-600 p-8 rounded-[40px] text-white shadow-xl shadow-emerald-100 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                  <SafeIcon name="TrendingUp" className="text-4xl text-emerald-200 mb-6" />
                  <h4 className="text-2xl font-black mb-2 uppercase tracking-tighter">ROI Prognose</h4>
                  <p className="text-xs text-emerald-100 leading-relaxed mb-6">
                    Ved fullstendig gjennomføring av planen forventer vi en <b>12-15% reduksjon i korttidsfravær</b> og en signifikant økning i operativ evne innen Q3.
                  </p>
                  <Button variant="secondary" className="w-full bg-white text-emerald-600 border-none font-black text-[10px] uppercase tracking-widest py-4">Se Business Case</Button>
                </div>

                <Card>
                  <CardHeader title="Neste Milepæl" />
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <SafeIcon name="Calendar" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">All-hands Møte</p>
                        <p className="text-xs text-slate-500">Dag 7: Presentasjon av funn</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};